import { FC, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-daisyui';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton';
import useDeletePracticeSet from '@/api/useDeletePracticeSet';

interface ExitPracticeSetButtonProps {
  practiceSetId: string;
}

const ExitPracticeSetButton: FC<ExitPracticeSetButtonProps> = ({ practiceSetId }) => {
  const navigate = useNavigate();
  const deletePracticeSet = useDeletePracticeSet();
  const [showExitModal, setShowExitModal] = useState(false);

  const handleExit = useCallback(async () => {
    try {
      await deletePracticeSet.mutateAsync({ practiceSetId });
      setShowExitModal(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to delete practice set:', error);
    }
  }, [practiceSetId, deletePracticeSet, navigate]);

  return (
    <>
      <button
        onClick={() => setShowExitModal(true)}
        className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl font-medium"
      >
        Exit
      </button>

      {/* Exit Confirmation Modal */}
      <Modal open={showExitModal} onClose={() => setShowExitModal(false)} className="max-w-md">
        <Modal.Header className="font-bold text-xl">Exit Practice Set?</Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to exit? All your progress will be lost and this practice set will be deleted. You will
            not be able to resume later.
          </p>
        </Modal.Body>
        <Modal.Actions className="flex gap-3">
          <button
            onClick={() => setShowExitModal(false)}
            className="flex-1 bg-white border border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-medium hover:bg-gray-50"
            disabled={deletePracticeSet.isPending}
          >
            Cancel
          </button>
          <LoadingButton
            loading={deletePracticeSet.isPending}
            onClick={handleExit}
            className="flex-1 bg-red-500 hover:bg-red-600 border-none text-white px-6 py-3 rounded-xl font-medium"
          >
            Exit
          </LoadingButton>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ExitPracticeSetButton;
